import { useQuery } from '@tanstack/react-query';
import { Button, Skeleton } from '@mlm/ui';
import { Plus, Trash2 } from 'lucide-react';
import { listMemberships } from '../../lib/api';
import { NumberField, SelectField } from './fields';

export type UnilevelConfig = {
  /** Fallback default per level (1-based: index 0 = level 1). */
  level_percents: number[];
  /** Tier × Level override: percentage_by_tier[receiverTierCode][level] = percent. */
  percentage_by_tier?: Record<string, Record<string, number>>;
  qualification: { strategy: 'always' } | { strategy: 'ppv_threshold'; required_ppv: number };
  compression: 'to_next_qualified' | 'off';
};

export function UnilevelEditor({
  value,
  onChange,
  readOnly,
}: {
  value: UnilevelConfig;
  onChange: (next: UnilevelConfig) => void;
  readOnly?: boolean;
}) {
  const isPpv = value.qualification.strategy === 'ppv_threshold';

  function setLevel(i: number, pct: number) {
    const next = [...value.level_percents];
    next[i] = pct;
    onChange({ ...value, level_percents: next });
  }
  function removeLevel(i: number) {
    // Drop the fallback row AND every tier's override at that (1-based) level,
    // then renumber deeper overrides down by one.
    const level = i + 1;
    const matrix = value.percentage_by_tier ?? {};
    const nextMatrix: Record<string, Record<string, number>> = {};
    for (const [tier, row] of Object.entries(matrix)) {
      const nextRow: Record<string, number> = {};
      for (const [lvlStr, pct] of Object.entries(row)) {
        const lvl = Number(lvlStr);
        if (lvl === level) continue;
        nextRow[String(lvl > level ? lvl - 1 : lvl)] = pct;
      }
      if (Object.keys(nextRow).length > 0) nextMatrix[tier] = nextRow;
    }
    onChange({
      ...value,
      level_percents: value.level_percents.filter((_, idx) => idx !== i),
      percentage_by_tier: nextMatrix,
    });
  }
  function addLevel() {
    onChange({ ...value, level_percents: [...value.level_percents, 0] });
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SelectField
          label="Qualification"
          hint="`always` pays every ancestor. `ppv_threshold` skips slots below the configured PPV in the current period."
          value={value.qualification.strategy}
          disabled={readOnly}
          onChange={(strategy) => {
            if (strategy === 'always') {
              onChange({ ...value, qualification: { strategy: 'always' } });
            } else {
              onChange({
                ...value,
                qualification: {
                  strategy: 'ppv_threshold',
                  required_ppv:
                    'required_ppv' in value.qualification ? value.qualification.required_ppv : 100,
                },
              });
            }
          }}
          options={[
            { value: 'always', label: 'Always qualify' },
            { value: 'ppv_threshold', label: 'PPV threshold' },
          ]}
        />
        {isPpv ? (
          <NumberField
            label="Required PPV"
            hint="Minimum personal PV in the period to earn at any level."
            value={'required_ppv' in value.qualification ? value.qualification.required_ppv : 0}
            disabled={readOnly}
            onChange={(v) =>
              onChange({
                ...value,
                qualification: { strategy: 'ppv_threshold', required_ppv: v ?? 0 },
              })
            }
            min={0}
            step={1}
          />
        ) : (
          <div />
        )}
        <SelectField
          label="Compression"
          hint="`to_next_qualified` skips unqualified ancestors and walks to the next one. There is NO tail-dump roll-up: levels with no upline are unpaid (legacy parity)."
          value={value.compression}
          disabled={readOnly}
          onChange={(compression) => onChange({ ...value, compression })}
          options={[
            { value: 'to_next_qualified', label: 'to_next_qualified' },
            { value: 'off', label: 'off' },
          ]}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium">Default rate per level</h3>
            <p className="text-[10px] text-muted-foreground">
              Level 1 = direct sponsor. % of the purchase PV. Used for any tier without an explicit
              override below. Adding/removing a level here also adds/removes that column in the
              matrix.
            </p>
          </div>
          {!readOnly ? (
            <Button size="sm" variant="outline" onClick={addLevel}>
              <Plus className="h-3 w-3 mr-1" />
              Add level
            </Button>
          ) : null}
        </div>
        <table className="w-full text-sm border rounded-md overflow-hidden">
          <thead className="text-left text-muted-foreground bg-muted/40 text-xs">
            <tr>
              <th className="px-3 py-2 font-medium w-16">Level</th>
              <th className="px-3 py-2 font-medium">Percent</th>
              <th className="px-3 py-2 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {value.level_percents.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-3 py-4 text-xs text-muted-foreground text-center">
                  No levels configured. Click "Add level" to start.
                </td>
              </tr>
            ) : (
              value.level_percents.map((pct, i) => (
                <tr key={i} className="border-t">
                  <td className="px-3 py-2 text-xs font-mono">L{i + 1}</td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      className="h-8 w-32 px-2 rounded-md border bg-background text-sm disabled:opacity-50"
                      value={pct}
                      disabled={readOnly}
                      step={0.01}
                      min={0}
                      max={100}
                      onChange={(e) => setLevel(i, Number(e.target.value) || 0)}
                    />{' '}
                    <span className="text-xs text-muted-foreground">%</span>
                  </td>
                  <td className="px-3 py-2">
                    {!readOnly ? (
                      <button
                        type="button"
                        onClick={() => removeLevel(i)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                        aria-label={`Remove level ${i + 1}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <UnilevelTierMatrix value={value} onChange={onChange} readOnly={readOnly} />
    </div>
  );
}

/** Per-tier × level override grid. Rows = receiver tier, columns = level. */
function UnilevelTierMatrix({
  value,
  onChange,
  readOnly,
}: {
  value: UnilevelConfig;
  onChange: (next: UnilevelConfig) => void;
  readOnly?: boolean;
}) {
  const tiersQ = useQuery({ queryKey: ['admin-memberships'], queryFn: listMemberships });
  const levels = value.level_percents.length;

  if (tiersQ.isLoading) return <Skeleton className="h-32 w-full" />;
  const tiers = tiersQ.data ?? [];

  function getCell(tier: string, level: number): number | '' {
    const v = value.percentage_by_tier?.[tier]?.[String(level)];
    return typeof v === 'number' ? v : '';
  }
  function setCell(tier: string, level: number, raw: string) {
    const matrix: Record<string, Record<string, number>> = {};
    for (const [t, row] of Object.entries(value.percentage_by_tier ?? {})) matrix[t] = { ...row };
    const row = { ...(matrix[tier] ?? {}) };
    if (raw.trim() === '') delete row[String(level)];
    else row[String(level)] = Number(raw) || 0;
    if (Object.keys(row).length === 0) delete matrix[tier];
    else matrix[tier] = row;
    onChange({ ...value, percentage_by_tier: matrix });
  }

  return (
    <div className="space-y-2">
      <div>
        <h3 className="text-sm font-medium">Per-tier overrides (Tier × Level)</h3>
        <p className="text-[10px] text-muted-foreground">
          Override the rate a specific receiver tier earns at a given level. Blank = use the default
          rate above. Matches legacy
          <code className="font-mono"> percentage_per_level</code> per tier.
        </p>
      </div>
      {tiers.length === 0 ? (
        <p className="text-xs text-muted-foreground border rounded-md p-4">
          No membership tiers yet. Create them under Members → Memberships.
        </p>
      ) : levels === 0 ? (
        <p className="text-xs text-muted-foreground border rounded-md p-4">
          Add at least one level above to configure per-tier overrides.
        </p>
      ) : (
        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 text-xs text-left text-muted-foreground">
                <th className="px-3 py-2 font-medium sticky left-0 bg-muted/40">Tier \ Level</th>
                {Array.from({ length: levels }, (_, i) => (
                  <th key={i} className="px-3 py-2 font-mono font-medium">
                    L{i + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier) => (
                <tr key={tier.id} className="border-t">
                  <td
                    className="px-3 py-1.5 text-xs font-mono font-medium bg-muted/20 sticky left-0"
                    title={tier.name}
                  >
                    {tier.code}
                  </td>
                  {Array.from({ length: levels }, (_, i) => {
                    const level = i + 1;
                    return (
                      <td key={i} className="px-2 py-1.5">
                        <input
                          type="number"
                          className="h-8 w-20 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                          placeholder={String(value.level_percents[i] ?? 0)}
                          value={getCell(tier.code, level)}
                          disabled={readOnly}
                          step={0.01}
                          min={0}
                          max={100}
                          onChange={(e) => setCell(tier.code, level, e.target.value)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function emptyUnilevelConfig(): UnilevelConfig {
  return {
    level_percents: [5, 3, 2, 1, 1],
    percentage_by_tier: {},
    qualification: { strategy: 'always' },
    compression: 'to_next_qualified',
  };
}
